import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ActionPanel = ({ onAddPosition, onExportReport, onRebalance }) => {
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [positionForm, setPositionForm] = useState({
    symbol: '',
    quantity: '',
    purchasePrice: '',
    purchaseDate: '',
    notes: ''
  });

  const cryptoOptions = [
    { value: 'BTC', label: 'Bitcoin (BTC)' },
    { value: 'ETH', label: 'Ethereum (ETH)' },
    { value: 'ADA', label: 'Cardano (ADA)' },
    { value: 'SOL', label: 'Solana (SOL)' },
    { value: 'DOT', label: 'Polkadot (DOT)' },
    { value: 'LINK', label: 'Chainlink (LINK)' },
    { value: 'MATIC', label: 'Polygon (MATIC)' },
    { value: 'AVAX', label: 'Avalanche (AVAX)' }
  ];

  const exportOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'csv', label: 'CSV Data' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'tax', label: 'Tax Report' }
  ];

  const handleFormChange = (field, value) => {
    setPositionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddPosition = () => {
    if (positionForm?.symbol && positionForm?.quantity && positionForm?.purchasePrice) {
      onAddPosition(positionForm);
      setPositionForm({
        symbol: '',
        quantity: '',
        purchasePrice: '',
        purchaseDate: '',
        notes: ''
      });
      setShowAddPosition(false);
    }
  };

  const handleExport = (format) => {
    onExportReport(format);
    setShowExportOptions(false);
  };

  const quickActions = [
    {
      icon: 'Plus',
      label: 'Add Position',
      description: 'Record a new cryptocurrency purchase',
      action: () => setShowAddPosition(true),
      variant: 'default'
    },
    {
      icon: 'Download',
      label: 'Export Report',
      description: 'Download portfolio data and reports',
      action: () => setShowExportOptions(true),
      variant: 'outline'
    },
    {
      icon: 'RotateCcw',
      label: 'Rebalance',
      description: 'Get portfolio rebalancing suggestions',
      action: onRebalance,
      variant: 'outline'
    },
    {
      icon: 'Calculator',
      label: 'Tax Calculator',
      description: 'Calculate tax implications',
      action: () => window.location.href = '/tax-calculator',
      variant: 'ghost'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Quick Actions</h2>
      </div>
      {/* Quick Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {quickActions?.map((action, index) => (
          <button
            key={index}
            onClick={action?.action}
            className="p-4 bg-card border border-border rounded-lg hover:shadow-elevation-2 transition-standard text-left group"
          >
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center group-hover:bg-primary/20 transition-standard">
                <Icon name={action?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-card-foreground mb-1">
                  {action?.label}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {action?.description}
                </p>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-foreground transition-standard" />
            </div>
          </button>
        ))}
      </div>
      {/* Add Position Modal */}
      {showAddPosition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-card-foreground">Add New Position</h3>
              <button
                onClick={() => setShowAddPosition(false)}
                className="text-muted-foreground hover:text-foreground transition-standard"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <Select
                label="Cryptocurrency"
                placeholder="Select cryptocurrency"
                options={cryptoOptions}
                value={positionForm?.symbol}
                onChange={(value) => handleFormChange('symbol', value)}
                required
              />

              <Input
                label="Quantity"
                type="number"
                placeholder="0.00000000"
                value={positionForm?.quantity}
                onChange={(e) => handleFormChange('quantity', e?.target?.value)}
                required
              />

              <Input
                label="Purchase Price (USD)"
                type="number"
                placeholder="0.00"
                value={positionForm?.purchasePrice}
                onChange={(e) => handleFormChange('purchasePrice', e?.target?.value)}
                required
              />

              <Input
                label="Purchase Date"
                type="date"
                value={positionForm?.purchaseDate}
                onChange={(e) => handleFormChange('purchaseDate', e?.target?.value)}
              />

              <Input
                label="Notes (Optional)"
                placeholder="Add any notes about this position"
                value={positionForm?.notes}
                onChange={(e) => handleFormChange('notes', e?.target?.value)}
              />
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddPosition(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleAddPosition}
                className="flex-1"
                disabled={!positionForm?.symbol || !positionForm?.quantity || !positionForm?.purchasePrice}
              >
                Add Position
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Export Options Modal */}
      {showExportOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-card-foreground">Export Report</h3>
              <button
                onClick={() => setShowExportOptions(false)}
                className="text-muted-foreground hover:text-foreground transition-standard"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-3">
              {exportOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => handleExport(option?.value)}
                  className="w-full p-3 bg-muted/50 hover:bg-muted rounded-md text-left transition-standard"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-card-foreground">
                      {option?.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={() => setShowExportOptions(false)}
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Portfolio Insights */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-medium text-card-foreground mb-4">Portfolio Insights</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-3 bg-success/10 rounded-md">
            <Icon name="TrendingUp" size={16} className="text-success mt-0.5" />
            <div>
              <p className="text-sm font-medium text-success">Strong Performance</p>
              <p className="text-xs text-muted-foreground">
                Your portfolio is outperforming Bitcoin by 12.5% this month
              </p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 p-3 bg-warning/10 rounded-md">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warning">Concentration Risk</p>
              <p className="text-xs text-muted-foreground">
                Consider diversifying - 73% of portfolio is in top 2 assets
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-primary/10 rounded-md">
            <Icon name="Lightbulb" size={16} className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary">Rebalancing Opportunity</p>
              <p className="text-xs text-muted-foreground">
                Consider taking profits on ETH and adding to underweight positions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPanel;